import BaseComponent from 'bootstrap/js/src/base-component.js';
import EventHandler from 'bootstrap/js/src/dom/event-handler.js';
import SelectorEngine from 'bootstrap/js/src/dom/selector-engine.js';
import { defineJQueryPlugin } from 'bootstrap/js/src/util/index.js';

const NAME = 'otpInput';
const DATA_KEY = 'bs.otpInput';
const EVENT_KEY = `.${DATA_KEY}`;

const EVENT_INPUT = `input${EVENT_KEY}`;
const EVENT_COMPLETE = `complete${EVENT_KEY}`;

const SELECTOR_OTP = '[data-bs-otp]';
const SELECTOR_INPUT = '.otp-input';
const CLASS_SLOTS = 'otp-slots';
const CLASS_GROUP = 'otp-group';
const CLASS_SLOT = 'otp-slot';
const CLASS_CHAR = 'otp-char';
const CLASS_CURSOR = 'otp-cursor';
const CLASS_SEPARATOR = 'otp-separator';
const CLASS_SLOT_ACTIVE = 'otp-slot-active';
const CLASS_SLOT_FILLED = 'otp-slot-filled';
const CLASS_DISABLED = 'disabled';

const PATTERNS = {
  numeric: /\D/g,
  alpha: /[^a-zA-Z]/g,
  alphanumeric: /[^a-zA-Z0-9]/g
};

const INPUT_PATTERNS = {
  numeric: '[0-9]',
  alpha: '[a-zA-Z]',
  alphanumeric: '[a-zA-Z0-9]'
};

class OtpInput extends BaseComponent {
  constructor(element, config) {
    super(element, config);

    this._input = SelectorEngine.findOne(SELECTOR_INPUT, this._element);
    if (!this._input) return;

    this._length = this._input.maxLength > 0 ? this._input.maxLength : 6;
    this._type = this._element.dataset.bsType || 'numeric';
    const charPattern = INPUT_PATTERNS[this._type] || INPUT_PATTERNS.numeric;
    this._input.pattern = `${charPattern}{${this._length}}`;
    this._mask = this._element.dataset.bsMask === 'true';
    this._groups = this._element.dataset.bsGroups
      ? String(this._element.dataset.bsGroups).split(',').map(Number)
      : [this._length];

    this._slots = [];
    this._buildSlots();
    this._bindEvents();
    this._render();
  }

  // Public API

  getValue() {
    return this._input.value;
  }

  setValue(value) {
    const pattern = PATTERNS[this._type] || PATTERNS.numeric;
    this._input.value = String(value).replace(pattern, '').slice(0, this._length);
    this._render();
  }

  clear() {
    this._input.value = '';
    this._render();
    this._input.focus();
  }

  // Private

  _buildSlots() {
    // Hide real input visually but keep it accessible
    this._input.style.cssText =
      'position:absolute;opacity:0;pointer-events:none;width:1px;height:1px;overflow:hidden;white-space:nowrap;';

    const slotsWrapper = document.createElement('div');
    slotsWrapper.classList.add(CLASS_SLOTS);
    slotsWrapper.setAttribute('aria-hidden', 'true');

    let slotIndex = 0;
    for (const [gi, groupLen] of this._groups.entries()) {
      const groupEl = document.createElement('div');
      groupEl.classList.add(CLASS_GROUP);

      for (let i = 0; i < groupLen; i++) {
        const slot = document.createElement('div');
        slot.classList.add(CLASS_SLOT);
        slot.dataset.index = slotIndex;

        const char = document.createElement('span');
        char.classList.add(CLASS_CHAR);

        const cursor = document.createElement('span');
        cursor.classList.add(CLASS_CURSOR);

        slot.append(char, cursor);
        groupEl.append(slot);
        this._slots.push({ slot, char });
        slotIndex++;
      }

      slotsWrapper.append(groupEl);

      if (gi < this._groups.length - 1) {
        const sep = document.createElement('span');
        sep.classList.add(CLASS_SEPARATOR);
        sep.textContent = '–';
        slotsWrapper.append(sep);
      }
    }

    this._element.append(slotsWrapper);
    this._slotsWrapper = slotsWrapper;
  }

  _bindEvents() {
    EventHandler.on(this._slotsWrapper, 'click', () => {
      if (this._input.disabled) return;
      this._input.focus();
      const len = this._input.value.length;
      this._input.setSelectionRange(len, len);
    });

    EventHandler.on(this._input, 'focus', () => this._render());
    EventHandler.on(this._input, 'blur', () => this._render());

    EventHandler.on(this._input, 'input', () => {
      const pattern = PATTERNS[this._type] || PATTERNS.numeric;
      this._input.value = this._input.value.replace(pattern, '').slice(0, this._length);
      this._render();

      EventHandler.trigger(this._element, EVENT_INPUT, { value: this._input.value });

      if (this._input.value.length === this._length) {
        EventHandler.trigger(this._element, EVENT_COMPLETE, { value: this._input.value });
      }
    });

    EventHandler.on(this._input, 'keydown', (e) => {
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        requestAnimationFrame(() => this._render());
      }
    });
  }

  _render() {
    const val = this._input.value;
    const focused = document.activeElement === this._input;
    const activeIdx = focused ? Math.min(val.length, this._length - 1) : -1;
    const isDisabled = this._input.disabled;

    this._slotsWrapper.classList.toggle(CLASS_DISABLED, isDisabled);

    for (const [i, { slot, char }] of this._slots.entries()) {
      const ch = val[i] || '';
      char.textContent = this._mask && ch ? '•' : ch;
      slot.classList.toggle(CLASS_SLOT_ACTIVE, i === activeIdx && !isDisabled);
      slot.classList.toggle(CLASS_SLOT_FILLED, Boolean(ch));
    }
  }

  // Static

  static get NAME() {
    return NAME;
  }

  static jQueryInterface(config, ...args) {
    return this.each(function () {
      const data = OtpInput.getOrCreateInstance(this);
      if (typeof config === 'string') {
        if (typeof data[config] === 'undefined') {
          throw new TypeError(`No method named "${config}"`);
        }

        data[config](...args);
      }
    });
  }
}

// Auto-init on DOMContentLoaded
EventHandler.on(globalThis, 'load', () => {
  for (const el of SelectorEngine.find(SELECTOR_OTP)) {
    OtpInput.getOrCreateInstance(el);
  }
});

defineJQueryPlugin(OtpInput);

export default OtpInput;
